import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch } from 'react-redux'

import { ContextMenu, MenuItem } from "@progress/kendo-react-layout";

import PagingGridToolbar from 'views/widgets/PagingGridToolbar.jsx';
import PagingGrid from 'views/widgets/PagingGrid.jsx';
import LoadingPanel from 'views/widgets/LoadingPanel.jsx';
import SearchWidget from "views/widgets/SearchWidget.jsx";

import { addMessage } from '../../messages/messageSlice'
import useSession from '../../hooks/useSession';
import { getInitialSearch } from 'views/widgets/gridColumnManager';

const SelectionSearchView = forwardRef((props, ref) => {
	
	const { elemId, url, label, type, columns, sort, take, additionalUrlParams,
		isSelectable = true, selectionMode = "multiple",
		gridClass = "constituo-search-grid-container",
		selectedState, setSelectedState,
		hideHeader, toolTip, checkboxColumnWidth="50px" } = props;
		
	const prefKey = type.toUpperCase() + "_SELECTION_GRID";
	const { getPreference } = useSession();
		
	const initialApiState = {
		pending: null, 
		lastSuccess: null
	};
	const initialPageState = {
		take: take === undefined ? 50 : take,
		skip: 0,
		sort: sort,
		searchStr: getInitialSearch(getPreference, prefKey + "_SEARCH")
	};	
	const initialState = {
		apiState: initialApiState,
		selectedItems: [],
		page: initialPageState
	}
	const [ state, setState ] = useState(initialState);
	const [ showContextMenu, setShowContextMenu ] = useState(false);
	
	const [ results, setResults ] = useState({ data: [], total: 0 });
	const [ masked, setMasked ] = useState(false);
	const [ selectedCell, setSelectedCell ] = useState("");
	
	const offset = useRef({
		left: 0,
		top: 0,		
	});
			
	const pagingGrid = useRef();
	
	const restoreDefaultLayout = () => {
		pagingGrid.current.restoreDefaultLayout();
	}

	const handleContextMenuOpen = (e) => {
		e.preventDefault();
		offset.current = {
			left: e.pageX,
			top: e.pageY,
		};
		setShowContextMenu(true);
	}
	
	const handleCloseContextMenu = () => {
		setShowContextMenu(false);
	}
	
	const handleCopyToClipboard = () => {
    	navigator.clipboard.writeText(selectedCell);
	};
	
	const handleOnSelectContextMenu = (e) => {				
		const action = e.item.data.action;
		switch (action) {
			case "copyText":
				handleCopyToClipboard();
				break;
			case "refresh":
				refresh();
				break;
			default:
				break;
		}
		setShowContextMenu(false);
	};
	
	const handleContextMenu = (event) => {
		handleContextMenuOpen(event.syntheticEvent);
		const { dataItem, field } = event;
		if (field) {
			const cell = dataItem[field];			
			setSelectedCell(cell);
		}			
	};
		
	const baseUrl = API_URL + url;
  	const init = {
		credentials: 'include',
    	method: "GET",
    	accept: "application/json",
		credentials: "include",
		mode: "cors",
		cache: "no-store",
    	headers: {},
  	};
  	
  	let isSelectionExpanded = false;
  	let selectionExpanded = [];
  	
  	const dispatch = useDispatch();
  	
	useImperativeHandle(ref, () => ({
		getSelected() {
        	return state.selectedItems;
        },
        getSelectedFullObjects() {
			return getSelectedFullObjects();
		},
        gridRefresh() {
			refresh();
		},
		mask() {
			updateMask(true);
		},
		unmask() {
			updateMask(false);
		}
	}));
  		
	// *** handle events
	function selectionChanged(e) {
		const selected = [];
		
		for (let key in e) {			
			if (e[key]) {
				selected.push(key);
			}
		}
		setState({...state, selectedItems: selected});	
	};
	
	function expandedChanged(e) {		
		let newData = results.data.map((result) => {
			if(result.id === e.dataItem.id) {
				result.expanded = !e.dataItem.expanded;
			}
			return result;
		});
		
		setResults({...results, data: newData});
	}
	
	function getSelectedFullObjects() {
		let selected = [];
		for (let i = 0; i < state.selectedItems.length; i++) { 
			for (let j = 0; j < results.data.length; j++) { 
				if (state.selectedItems[i] == results.data[j].id) {
					selected.push(results.data[j]);
					break;
				}
			}
		}
				
		return selected;
	};
	
	// *** handle data fetching
	function refresh(clearSelected) {
		setState({...state, selectedItems: clearSelected ? [] : state.selectedItems, apiState: {pending: null, lastSuccess: null}});
	};
	
	function requestDataIfNeeded() {
		// if there is a pending request or the request has already completed, just move on
    	if ( state.apiState.pending || getUrlParams() === state.apiState.lastSuccess ) {
      		return;
    	}

    	state.apiState.pending = getUrlParams();
    	updateMask(true);
    	fetch(baseUrl + state.apiState.pending, init
			).then((response) => {
				if (response.ok) { 
					return response.json(); 
				} else {
					throw new Error(response.status); 
				}
			}).then((json) => {
				state.apiState.lastSuccess = state.apiState.pending;
				state.apiState.pending = null;
				
				// if the response matches the request, finish up
        		if (getUrlParams() === state.apiState.lastSuccess) { 
          			dataReceived.call(undefined, {
            			data: json.results,
            			total: json.total
          			});
          			
          		// otherwise, keep checking for the response
        		} else {
          			requestDataIfNeeded();
				}
				updateMask(false);

        	}).catch((error) => {
				state.apiState.lastSuccess = state.apiState.pending;
				state.apiState.pending = null;
				updateMask(false);
				fetchFailed(error);		
			});
  	};
  	
	function getUrlParams() {
		let sortName = getSortName(state.page.sort[0].field);
		
		return `
			?searchStr=${state.page.searchStr || ''}
			&offset=${state.page.skip}
			&limit=${state.page.take}
			&sort=${sortName}
			&direction=${(state.page.sort[0].dir === "asc" ? 0 : 1)}
			${(additionalUrlParams ? additionalUrlParams : '')}
		`;
	};
	
	function getSortName(fieldName) {
		for (let i = 0; i < columns.length; i++) {
			let column = columns[i];
			if (fieldName === column.field) return column.sort;
		}
		return fieldName;
	};  
	
	function transformColumnData(columns, data) {
		columns.filter(column => column.transform).forEach(({field, transform}) => {			
			data.forEach((datum) => {				
				Object.entries(datum).forEach(([key, value]) => {					
					if(key === field) datum[key] = transform(value);				
				});				
			});			
		});				
	}	

 	function dataReceived(newResults) {
		dispatch(addMessage({ type: 'success', msg: label + ' successfully retrieved.' }));
		
		transformColumnData(columns, newResults.data);
    	setResults({ ...newResults });
  	};
	
	function fetchFailed(status) { 
		let errMsg = (status.message === '403') ? ("Unauthorized session!") : ("Failed to fetch " + label + ".  If this problem persists, please contact Support.");
		dispatch(addMessage({ type: 'error', msg: errMsg }));
		setResults({ data: [], total: 0 })
	};
	
	function updatePage(newPage) {
		state.page = {take: newPage.take, skip: newPage.skip, sort: state.page.sort, searchStr: state.page.searchStr};
		refresh();
	};
	
	function updateMask(maskedStatus) {
		setMasked(maskedStatus);
	};
	
	function getFullItems(ids) {
		if (isSelectionExpanded) return selectionExpanded;
		selectionExpanded = [];
		for (let i = 0; i < ids.length; i++) {
			for (let j = 0; j < results.data.length; j++) {
				if (ids[i] === results.data[j].id) {
					selectionExpanded.push(results.data[j]);
					break;
				}
			}
		}
		isSelectionExpanded = true;
		return selectionExpanded;
	};
	
	const searchWidget = () => {		
		return (
			<SearchWidget 
				state={state.page}
				prefKey={prefKey}
				refresh={refresh}
				toolTip={toolTip}
			/>
		);		
	}
	
	requestDataIfNeeded();
	return (
	  	<div
	  		className={`constituo-grid-view ${gridClass}`}
	  		id={elemId}
	  	>
	  		<PagingGridToolbar 
	  			type={type}
	  			label={label}
	  			url={url}
	  			columns={columns}
	  			sort={sort}
	  			selected={state.selectedItems}
	  			extraButtons={[]}
	  			getFullItems={getFullItems}
	  			refresh={refresh}
	  			updateMask={updateMask}  
	  			hideButtons={false}
	  			showGrid={"grid"}
	  			gridViewChanged={() => {}}
	  			searchWidget={searchWidget}
	  			restoreDefaultLayout={restoreDefaultLayout}
	  		/>
			<PagingGrid 
				ref={pagingGrid}
				prefKey={type.toUpperCase() + "_SELECTION_GRID"}
				hideHeader={hideHeader}
				handleContextMenu={handleContextMenu}
				columns={columns}
				results={results}
				page={state.page}
	  			pageSizes={[10, 20, 50, 100]}
				updatePage={updatePage}
				isSelectable={isSelectable}
				selectionChanged={selectionChanged}
				selectionMode={selectionMode}
				expandedChanged={expandedChanged}
				showGrid={"grid"}
				selectedState={selectedState}
				setSelectedState={setSelectedState}
				style={{
					boxShadow: "none",
    				WebkitBoxShadow: "none",
    				MozBoxShadow: "none"
    			}}
    			checkboxColumnWidth={checkboxColumnWidth}
			 />
			<ContextMenu
				show={showContextMenu}
				offset={offset.current}
				onSelect={handleOnSelectContextMenu}
				onClose={handleCloseContextMenu}
			>
				<MenuItem
					text="Copy Text"
					data={{
						action: "copyText",
					}}
					icon={"fa fa-fw fa-paste"} 
				/>
				<MenuItem
					text="Refresh"
					data={{
						action: "refresh",
					}}
					icon={"fa fa-fw fa-cw"} 
				/>				
			</ContextMenu>			 
			 { (state.apiState.pending || masked) && (
				<LoadingPanel /> 
			 )}
		</div>
	);
});

export default SelectionSearchView;
